import { observer } from 'mobx-react'
import React, { ReactElement } from 'react'

import {
   InfiniteScrollList,
   Loader,
   useCustomT
} from '@flowwai-frontend/shared/common-components'
import { useAppTheme } from '@flowwai-frontend/bps-officer-web/style-guide'

import { SearchResultType } from '../../../types'

import { SEARCH_RESULT_SCROLL_TARGET_ID } from '../constants'
import {
   infiniteLoadingViewContainerClassName,
   resultsHeadingClassName,
   searchResultsListContainerClassName
} from '../styles'
import '../styles.css'

import GSResultItem from './GSResultItem'
import GSResultItemForTemplateContent from './GSResultItemForTemplateContent'

interface Props {
   totalSearchResults: number
   searchResults: SearchResultType[]
   fetchMoreResults: (offset: number) => void
   isFromTemplateContent: boolean
   onClickGSResultItem: (pipelineItemId: string, pipelineId?: string) => void
}

const GSResultsList = (props: Props): React.ReactElement => {
   const {
      totalSearchResults,
      searchResults,
      isFromTemplateContent,
      fetchMoreResults,
      onClickGSResultItem
   } = props

   const t = useCustomT('globalSearch:searchModal')
   const { theme } = useAppTheme()
   const { searchResultsHeadingColor } = theme.globalSearch.searchModal

   const renderLoadingView = (): ReactElement => (
      <div className={infiniteLoadingViewContainerClassName}>
         <Loader />
      </div>
   )

   const hasMore = totalSearchResults > searchResults.length

   const getMoreSearchResults = (): void => {
      fetchMoreResults(searchResults.length)
   }

   return (
      <div
         id={SEARCH_RESULT_SCROLL_TARGET_ID}
         className={`${searchResultsListContainerClassName} search-results-list`}
         data-testid='officer-global-search-results-list'
      >
         <h3
            className={`${resultsHeadingClassName} ${searchResultsHeadingColor}`}
            data-testid='officer-global-search-results-heading'
         >
            {t('searchResultsHeading')}
         </h3>
         <InfiniteScrollList
            dataLength={searchResults.length}
            hasMore={hasMore}
            loader={renderLoadingView()}
            next={getMoreSearchResults}
            scrollThreshold={0.6}
            scrollableTarget={SEARCH_RESULT_SCROLL_TARGET_ID}
            style={{ padding: '0px', margin: '0px auto' }}
         >
            {searchResults.map((resultItem: SearchResultType) => (
               <React.Fragment key={resultItem.pipelineItemId}>
                  {isFromTemplateContent ? (
                     <GSResultItemForTemplateContent
                        gsResultItem={resultItem}
                        onClickGSResultItem={onClickGSResultItem}
                     />
                  ) : (
                     <GSResultItem
                        gsResult={resultItem}
                        onClickGSResultItem={onClickGSResultItem}
                     />
                  )}
               </React.Fragment>
            ))}
         </InfiniteScrollList>
      </div>
   )
}

export default observer(GSResultsList)
